import { useDebounce } from '@uidotdev/usehooks';
import { isEqual } from 'lodash';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  GridColDef,
  GridColumnHeaderParams,
  GridFilterItem,
  GridValidRowModel,
  gridFilterModelSelector,
  useGridApiContext,
} from '@mui/x-data-grid-pro';

type HeaderWrapperProps = {
  currentHeaderParams: GridColumnHeaderParams<GridValidRowModel>;
  originColumn: GridColDef<GridValidRowModel>;
};

const HeaderWrapper: FC<HeaderWrapperProps> = ({
  currentHeaderParams,
  originColumn,
}) => {
  const apiRef = useGridApiContext();
  const filterModel = gridFilterModelSelector(apiRef.current.state);

  const { t } = useTranslation(['forms']);

  const inputRef = useRef<HTMLInputElement>(null);
  const inputValueDebouncedRef = useRef<string | null>(null);

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);

  const [inputValue, setInputValue] = useState('');
  const inputValueDebounced = useDebounce(inputValue, 500);

  const isInputLabelShrink = isInputFocused || inputValue !== '';

  const handleFilterModelUpdate = useCallback(
    (inputValue: string) => {
      let newFilterItems: GridFilterItem[];

      const filter = filterModel.items.find(
        (item) => item.field === currentHeaderParams.field,
      );

      if (filter) {
        newFilterItems = filterModel.items.map((item) =>
          item.id === filter.id ? { ...item, value: inputValue } : item,
        );
      } else {
        newFilterItems = [
          ...filterModel.items,
          {
            id: uuidv4(),
            field: currentHeaderParams.field,
            operator: 'ilike',
            value: inputValue,
          },
        ];
      }

      if (!inputValue) {
        newFilterItems = newFilterItems.filter(
          (item) => item.field !== currentHeaderParams.field,
        );
      }

      apiRef.current.setFilterModel({
        ...filterModel,
        items: newFilterItems,
      });
    },
    [apiRef, currentHeaderParams.field, filterModel],
  );

  useEffect(() => {
    if (!isEqual(inputValueDebounced, inputValueDebouncedRef.current)) {
      inputValueDebouncedRef.current = inputValueDebounced;

      handleFilterModelUpdate(inputValueDebounced);
    }
  }, [handleFilterModelUpdate, inputValueDebounced]);

  useEffect(() => {
    const filter = filterModel.items.find(
      (item) => item.field === currentHeaderParams.field,
    );

    setInputValue((filter?.value as string) || '');
  }, [currentHeaderParams.field, filterModel]);

  const cellContent = originColumn.renderHeader ? (
    originColumn.renderHeader(currentHeaderParams)
  ) : (
    <Stack
      direction='row'
      alignItems='center'
      gap={1}
      sx={{ width: '100%', pl: 1 }}
      onMouseEnter={() => setIsHeaderHovered(true)}
      onMouseLeave={() => setIsHeaderHovered(false)}
    >
      {currentHeaderParams.colDef.filterable ? (
        <>
          <TextField
            inputRef={inputRef}
            label={t(currentHeaderParams.field)}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => {
              setIsInputFocused(false);
              handleFilterModelUpdate(inputValue);
            }}
            onChange={(e) => setInputValue(e.target.value)}
            InputLabelProps={{
              shrink: isInputLabelShrink,
              sx: { fontSize: 14 },
            }}
            InputProps={{
              disableUnderline: true,
              startAdornment: isInputLabelShrink ? (
                <SearchIcon
                  sx={{
                    width: 16,
                    height: 16,
                    mr: 1,
                    transition: 'opacity 0.3s',
                    color: 'text.secondary',
                  }}
                  className='icon'
                />
              ) : null,
              sx: { 'label + &': { mt: 3 } },
            }}
            value={inputValue}
            size='small'
            variant='standard'
            fullWidth
          />

          {isHeaderHovered && !isInputLabelShrink && (
            <IconButton onClick={() => inputRef.current?.focus()}>
              <SearchIcon
                sx={{
                  width: 16,
                  height: 16,
                  color: 'text.secondary',
                }}
                className='icon'
              />
            </IconButton>
          )}

          {isHeaderHovered && !isInputLabelShrink && (
            <IconButton
              onClick={() => {
                apiRef.current.showColumnMenu(currentHeaderParams.field);
              }}
            >
              <KeyboardArrowDownIcon
                sx={{
                  width: 16,
                  height: 16,
                  color: 'text.secondary',
                }}
                className='icon'
              />
            </IconButton>
          )}

          {inputValue !== '' && (
            <IconButton
              size='small'
              sx={{ ml: 1, mb: 0.25, alignSelf: 'flex-end' }}
              onClick={() => {
                setInputValue('');
                handleFilterModelUpdate('');
              }}
            >
              <ClearIcon sx={{ width: 16, height: 16 }} />
            </IconButton>
          )}
        </>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          {t(currentHeaderParams.field)}
        </Typography>
      )}
    </Stack>
  );

  return cellContent;
};

export default HeaderWrapper;
